import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Loader2, CheckCircle2, Lock } from 'lucide-react';
import { api } from '../App';
import { ASSETS } from '../constants/site';

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      await api.post('/auth/reset-password', { token, password });
      setDone(true);
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === 'string' ? d : 'Could not reset the password. Request a fresh link.');
    } finally {
      setBusy(false);
    }
  };

  const inputCls = 'w-full bg-[#0B1528]/70 border border-slate-700/60 focus:border-amber-500/60 rounded-xl pl-11 pr-4 py-3 text-sm text-slate-200 outline-none transition-colors placeholder:text-slate-600';

  return (
    <div className="min-h-screen bg-[#050B14] circuit-bg flex items-center justify-center px-6">
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
        className="gold-ring rounded-3xl bg-[#0F1C33]/80 p-10 max-w-md w-full text-center" data-testid="reset-password-page">
        <img src={ASSETS.icon} alt="GSLAB" className="h-14 w-14 rounded-xl mx-auto mb-6" />
        {done ? (
          <>
            <CheckCircle2 size={40} className="text-emerald-400 mx-auto mb-5" />
            <h1 className="font-display font-bold text-2xl mb-3">Password updated.</h1>
            <p className="text-sm text-slate-400 mb-8">You can sign in with your new password now.</p>
            <Link to="/login" data-testid="reset-goto-login" className="inline-block bg-gradient-to-r from-[#D4AF37] to-[#F3C64F] text-[#050B14] font-bold rounded-full px-8 py-3 text-sm">
              Go to sign in
            </Link>
          </>
        ) : !token ? (
          <>
            <h1 className="font-display font-bold text-2xl mb-3">Invalid link</h1>
            <p className="text-sm text-slate-400 mb-8">This reset link is missing its token. Request a new one from the sign-in page.</p>
            <Link to="/login" className="text-sm text-amber-300 hover:underline">Back to sign in</Link>
          </>
        ) : (
          <>
            <h1 className="font-display font-bold text-2xl mb-3">Set a new password</h1>
            <p className="text-sm text-slate-400 mb-8">At least 8 characters.</p>
            <form onSubmit={submit} className="space-y-3 text-left">
              <div className="relative">
                <Lock size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />
                <input data-testid="reset-password-input" required type="password" minLength={8} placeholder="New password" value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} />
              </div>
              <div className="relative">
                <Lock size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />
                <input data-testid="reset-confirm-input" required type="password" minLength={8} placeholder="Confirm new password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className={inputCls} />
              </div>
              {error && <p data-testid="reset-error" className="text-xs text-red-400 bg-red-500/10 border border-red-500/25 rounded-lg px-4 py-2.5">{error}</p>}
              <button data-testid="reset-submit-button" type="submit" disabled={busy}
                className="w-full inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#D4AF37] via-[#F3C64F] to-[#D4AF37] text-[#050B14] font-bold rounded-full px-6 py-3.5 disabled:opacity-60">
                {busy && <Loader2 size={16} className="animate-spin" />}
                Update password
              </button>
            </form>
          </>
        )}
      </motion.div>
    </div>
  );
}
